import React, { useEffect, useState } from 'react';
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module'
import { Layout, Loader, Box } from '@flexisaf/flexibull2';
import { useDispatch, useSelector } from 'react-redux';
import MainApp from 'containers';
import ResetPassword from 'components/ResetPassword';
import ConfirmUser from 'components/ConfirmUser';
import { getLocalAccessToken } from 'utils/helpers';
import { OrgSelection } from 'components/OrgSelection';
import LoginPage from 'containers/Login';
import QrCandidateView from 'containers/QRCandidateView';
import { Page404 } from 'components/Page404';
import { BlankPage } from 'components/OrgSelection/style';
import GoogleAuth from 'components/GoogleAuth';
import {
  checkingOrgLoading,
  checkingOrgError,
  selectOrgAliases,
} from 'redux/configurations/selectors';
import {
  isAuthOrganisationMismatch,
  migrateAdminStorage,
  replaceOrgSlugInPath,
  shouldRewriteRouteToPortalAlias,
} from 'utils/portalSlug';
import { logout } from 'utils/helpers';
import { menuItems } from 'routes';
import { CanFilter } from 'components/CanView';

import 'assets/css/app.css';
import 'assets/fonts/fontello/css/safapply.css';
import { checkOrg } from 'redux/configurations/actions';
import config from 'utils/config';
import SWUpdateAlert from 'components/SWUpdateAlert';
import {
  selectServiceWorkerRegistration,
  selectIsServiceWorkerUpdated
} from './redux/service-workers/selectors';
import { updateServiceWorker } from './redux/service-workers/actions';


ReactGA.initialize(config.GA_MEASUREMENT_ID);

function App() {
  const dispatch = useDispatch();
  const orgCheckLoading = useSelector(checkingOrgLoading);
  const orgCheckError = useSelector(checkingOrgError);
  const aliases = useSelector(selectOrgAliases);
  const accessToken = getLocalAccessToken();
  const { params } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const [orgData, setOrgData] = useState(null);
  const [portalSlug, setPortalSlug] = useState(null);

  useEffect(() => {
    TagManager.initialize({ gtmId: config.GTM_ID })
  }, []);

  useEffect(() => {
    if (orgCheckError && accessToken) {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgCheckError]);

  useEffect(() => {
    setOrgData(null);
    setPortalSlug(null);
  }, [params.orgName]);

  useEffect(() => {
    if (!params.orgName) {
      return;
    }
    dispatch(checkOrg(params.orgName, (data) => {
      setOrgData(data);
      const slug = data?.portalAlias || data?.id || params.orgName;
      setPortalSlug(slug);
    }));
    ReactGA.event({
      category: 'org name',
      action: params.orgName,
    });
  }, [dispatch, params.orgName]);

  useEffect(() => {
    if (
      !shouldRewriteRouteToPortalAlias({
        routeOrgName: params.orgName,
        portalAlias: portalSlug,
        resolvedCanonicalOrgId: orgData?.id,
        hasAccessToken: Boolean(accessToken),
      })
    ) {
      return;
    }
    migrateAdminStorage(params.orgName, portalSlug);
    const nextPath = replaceOrgSlugInPath(
      location.pathname,
      params.orgName,
      portalSlug
    );
    history.replace(`${nextPath}${location.search}${location.hash}`);
  }, [
    portalSlug,
    orgData,
    params.orgName,
    accessToken,
    history,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const activePortalSlug =
    portalSlug || (orgData?.portalAlias || orgData?.id) || params.orgName;

  const redirectToAvailableRoute = () => {
    const availableRoutes = CanFilter(menuItems(activePortalSlug), aliases);
    return availableRoutes?.[0]?.navlink || `/${activePortalSlug}/user-profile`;
  };

  const CheckOrgExistence = ({ ...props }) => {
    if (orgCheckLoading)
      return (
        <BlankPage>
          <Box margin="50vh auto">
            <Loader />
          </Box>
        </BlankPage>
      );
    if (orgCheckError) {
      return (
        <Route
          component={() => (
            <OrgSelection title="Organisation could not be found" />
          )}
        />
      );
    }
    return !accessToken ? (

      <LoginPage orgData={orgData} {...props} />
    ) : (
      <Redirect to={redirectToAvailableRoute()} />
    );
  };

  return (
    <Switch>
      <Route
        exact
        path="/:orgName"
        component={(props) => <CheckOrgExistence {...props} />}
      />
      <Route
        exact
        path="/:orgName/reset-request"
        component={(props) => <ResetPassword {...props} />}
      />
      <Route
        exact
        path="/:orgName/reset-password"
        component={(props) => <ResetPassword {...props} />}
      />
      <Route
        exact
        path="/:orgName/candidate-info/:id"
        component={(props) => <QrCandidateView {...props} />}
      />
      <Route
        exact
        path="/:orgName/confirm"
        component={(props) => <ConfirmUser {...props} />}
      />
      <Route
        exact
        path="/:orgName/404"
        component={(props) => <Page404 {...props} />}
      />
      <Route
        exact
        path="/:orgName/auth"
        component={(props) => <GoogleAuth {...props} />}
      />
      <Route
        path="/:orgName/*"
        component={(props) => {
          if (!accessToken) {
            return <Redirect to={`/${activePortalSlug}`} />;
          }
          const orgMismatch =
            orgCheckLoading ||
            !orgData?.id ||
            (accessToken && isAuthOrganisationMismatch(orgData.id));
          if (orgMismatch) {
            return (
              <BlankPage>
                <Box margin="50vh auto">
                  <Loader />
                </Box>
              </BlankPage>
            );
          }
          return <MainApp {...props} />;
        }}
      />
      <Route component={() => <div>Not Found</div>} />
    </Switch>
  );
}

const AdminApp = () => {
  const dispatch = useDispatch();
  const [updatingSW, setUpdatingSW] = useState(false);

  const serviceWorkerRegistration = useSelector(selectServiceWorkerRegistration)
  const isServiceWorkerUpdated = useSelector(selectIsServiceWorkerUpdated)

  const closeUpdate = () => {
    if (updatingSW) return;
    dispatch(updateServiceWorker(false));
  }

  const updateServiceApp = () => {
    const registrationWaiting = serviceWorkerRegistration?.waiting;
    
    if (registrationWaiting) {
      setUpdatingSW(true)
      registrationWaiting.postMessage({ type: 'SKIP_WAITING' });
      
      registrationWaiting.addEventListener('statechange', e => {
        if (e.target.state === 'activated') {
          closeUpdate();
          window.location.reload(true);
        }
      });
      window.location.reload(true);
    } else {
      closeUpdate();
    }
  };

  return(
    <Layout>
      
      <Router>
        <SWUpdateAlert
          open={isServiceWorkerUpdated}
          update={updateServiceApp}
          cancel={closeUpdate}
          updatingSW={updatingSW}
        />
        <Switch>
          <Route path="/:orgName/" component={App} />
          <Route
            component={() => <OrgSelection title="Organisation not Selected" />}
          />
        </Switch>
      </Router>
    </Layout>
  );
}

export default AdminApp;
